import { ApplicationDocument } from '../model/application.interface';
import { IApplicationEmailResponse, IReplyEmail, IResponseReplyEmail } from '../model/email-response.interface';
import { IEmail } from '../model/email.interface';
import { ApplicationEmailResponse } from './email-response.class';
import { Company } from './organization.class';
import { TranslationKeys } from '../model/translation-object.interface';
import { User } from '../model/user.interface';
import { FileResponse, IFile } from '../model/upload-files.model';
import { linkify } from '../resources/shared-functions';
import { IAgreement } from '../model/agreement.model';

export class Email {

  applicationEmailResponses: ApplicationEmailResponse[] = [];
  applicationDocuments: ApplicationDocument[];
  createdAt: string;
  subject: string;
  content: string;
  senderName: string;
  senderEmail: string;
  sortDate: Date;
  agreements: IAgreement[];
  opened: boolean;

  constructor(email: IEmail, company: Company, currentUser?: User) {
    const {
      applicationEmailResponses,
      createdAt,
      id,
      subject,
      content, senderCustomer, senderEnterpriseManager, applicationDocuments, applicationAgreements
    } = email;

    const sender = senderCustomer || senderEnterpriseManager;

    this.senderEmail = sender.email;

    const anonymousSender = sender.anonymousCustomer || sender.anonymousEnterpriseManager;
    this.senderName = anonymousSender ? company.name : sender.name;
    this.applicationDocuments = applicationDocuments;
    this.createdAt = createdAt;
    this.subject = subject;
    this.content = content;
    this.agreements = applicationAgreements;
    this.opened = false;

    if (content && !content.includes('</a>')) {
      this.content = linkify(content);
    }

    if (applicationEmailResponses?.length) {
      this.applicationEmailResponses = applicationEmailResponses
        .map((emailResponse: IApplicationEmailResponse) => new ApplicationEmailResponse(emailResponse, company, currentUser));
    }
  }

  public static setAttachments(uploadedFiles: FileResponse[], templateFiles: IFile[]): Record<string, string> {
    const attachments = {};

    templateFiles
      .forEach(({ name, file }: IFile) => {
        attachments[name] = file;
      });

    uploadedFiles
      .forEach(({ fileName, link }: FileResponse) => {
        attachments[fileName] = link;
      });

    return attachments;
  }

  public static setTemplateFiles(attachments: Record<string, string>): IFile[] {
    const templateFiles = [];

    if (attachments) {
      for (const [key, value] of Object.entries(attachments)) {
        templateFiles.push({ name: key, file: value });
      }
    }

    return templateFiles;
  }
}

export class ReplyEmail {

  jobTitle: string;
  companyLogo: string;
  senderName: string;
  subject: string;
  createdAt: string;
  id: number;
  content: string;
  applicationEmailResponses: number;
  language: TranslationKeys;
  applicationDocuments: IFile[];
  agreements: IAgreement[];

  constructor({
    application,
    senderCustomer,
    senderEnterpriseManager,
    subject,
    createdAt,
    id,
    content,
    applicationEmailResponses,
    applicationDocuments,
    applicationAgreements
  }: IReplyEmail
  ) {
    const job = application.job || application.universalJob;

    this.jobTitle = job.jobTitle;
    this.companyLogo = job.company.companyLogo;
    this.subject = subject;
    this.createdAt = createdAt;
    this.id = id;
    this.agreements = applicationAgreements;
    this.content = content;

    if (!content.includes('</a>')) {
      this.content = linkify(content);
    }

    this.applicationEmailResponses = applicationEmailResponses.length;
    this.language = job.language;
    this.applicationDocuments = applicationDocuments;

    const sender = senderCustomer || senderEnterpriseManager;
    const anonymousSender = sender?.anonymousCustomer || sender?.anonymousEnterpriseManager;
    this.senderName = anonymousSender ? job.company.name : sender?.name;
  }
}

export class ResponseReplyEmail {

  jobTitle: string;
  companyLogo: string;
  senderName: string;
  subject: string;
  createdAt: string;
  id: number;
  content: string;
  language: TranslationKeys;
  applicationDocuments: IFile[];
  agreements: IAgreement[];
  applicationEmailResponses: number;

  constructor({
    id,
    content,
    applicationAgreements,
    responseOnApplicationEmailResponse,
    applicationDocuments,
    createdAt,
    senderCustomer,
    senderEnterpriseManager,
    numberOfApplicationEmailResponses
  }: IResponseReplyEmail
  ) {

    this.id = id;
    this.content = content;
    this.createdAt = createdAt;
    this.applicationEmailResponses = numberOfApplicationEmailResponses;

    if (!content.includes('</a>')) {
      this.content = linkify(content);
    }

    this.language = TranslationKeys.EN;
    this.agreements = applicationAgreements;
    this.applicationDocuments = applicationDocuments;
    const applicationEmail = this.findApplicationEmail(responseOnApplicationEmailResponse);

    this.subject = applicationEmail.subject;
    this.jobTitle = applicationEmail.application?.job.jobTitle;
    this.companyLogo = applicationEmail.application.job.company.companyLogo;

    const sender = senderCustomer || senderEnterpriseManager;
    const anonymousSender = sender?.anonymousCustomer || sender?.anonymousEnterpriseManager;
    this.senderName = anonymousSender ? applicationEmail.application.job.company.name : sender?.name;
  }

  private findApplicationEmail(responseOnApplicationEmailResponse: IApplicationEmailResponse): IReplyEmail {
    if (responseOnApplicationEmailResponse.responseOnApplicationEmail) {
      return responseOnApplicationEmailResponse.responseOnApplicationEmail;
    };
    return this.findApplicationEmail(
      responseOnApplicationEmailResponse.responseOnApplicationEmailResponseReply.responseOnApplicationEmailResponse
    );
  }
}

export class ResponseEmails {
  hasResponses = false;
  hasUnreadResponses = false;
  numberOfOwnUnreadResponses = 0;
  numberOfUnreadResponsesFromOthers = 0;
}
